import React from "react";
import { Mail, MapPin, Clock, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const EMAIL = "info@riscocostruzioni.it";
const PHONE_HREF = "tel:+390810623700";
const PHONE_LABEL = "081 062 3700";

export default function Contatti() {
  const [form, setForm] = React.useState({ name: "", email: "", message: "", privacy_consent: true, website: "" });
  const [sending, setSending] = React.useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      // Evento GA4: conversione
      track("contatto_inviato");
      toast.success("Messaggio inviato! Ti risponderemo entro 24-48 ore. Controlla anche la tua email.");
      setForm({ name: "", email: "", message: "", privacy_consent: true, website: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio, riprova.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <SEO
        title="Contatti — Impresa edile a Napoli e provincia | Ri.Sco."
        description="Contatta Ri.Sco. s.r.l. a Napoli. Telefono 081 062 3700, email info@riscocostruzioni.it. Preventivo gratuito e sopralluogo per ristrutturazioni, edificazioni e operazioni immobiliari in Napoli e provincia."
        path="/contatti"
      />
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 border-b border-risco-border" data-testid="contatti-page">
        <div className="container-x">
          <div className="overline mb-6">Contatti</div>
          <h1 className="font-heading text-5xl sm:text-6xl md:text-8xl lg:text-9xl tracking-tighter text-risco-ink leading-[0.95]">
            <span className="font-display italic">Parliamone.</span>
          </h1>
          <p className="text-stone-600 mt-8 max-w-2xl text-lg leading-relaxed">Chiamaci allo <a href={PHONE_HREF} className="text-risco-accent font-semibold hover:underline">{PHONE_LABEL}</a> o scrivici via email. Rispondiamo entro 24-48 ore lavorative in tutta Napoli e provincia.</p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container-x grid lg:grid-cols-2 gap-10">
          <div className="space-y-4">
            <a href={PHONE_HREF} data-testid="contact-phone-card" className="risco-card p-6 flex items-start gap-4 hover:border-risco-accent transition-colors bg-risco-ink text-risco-bg">
              <Phone className="w-6 h-6 text-risco-accent mt-1" />
              <div>
                <div className="font-heading text-lg text-risco-bg">Telefono</div>
                <div className="text-risco-bg text-2xl md:text-3xl font-heading tracking-tight mt-1">{PHONE_LABEL}</div>
                <div className="text-stone-400 text-xs mt-2">Chiama subito per un sopralluogo gratuito</div>
              </div>
            </a>
            <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="risco-card p-6 flex items-start gap-4 hover:border-risco-accent transition-colors">
              <Mail className="w-6 h-6 text-risco-accent mt-1" />
              <div>
                <div className="font-heading text-lg text-risco-ink">Email</div>
                <div className="text-stone-700 mt-1">{EMAIL}</div>
                <div className="text-stone-500 text-xs mt-2">Rispondiamo entro 24-48 ore lavorative</div>
              </div>
            </a>
            <div className="risco-card p-6 flex items-start gap-4">
              <MapPin className="w-6 h-6 text-risco-accent mt-1" />
              <div>
                <div className="font-heading text-lg text-risco-ink">Sede</div>
                <div className="text-stone-700 mt-1">Via Martiri di Pietrarsa 20<br/>80046 San Giorgio a Cremano (NA)</div>
                <div className="text-stone-500 text-xs mt-2">Operiamo in tutta Napoli e provincia</div>
              </div>
            </div>
            <div className="risco-card p-6 flex items-start gap-4">
              <Clock className="w-6 h-6 text-risco-accent mt-1" />
              <div>
                <div className="font-heading text-lg text-risco-ink">Orari</div>
                <div className="text-stone-700 mt-1">Lun-Ven 9:00 - 18:00 · Sab 9:00 - 13:00 su appuntamento</div>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="risco-card p-8 space-y-4" data-testid="contact-form">
            <div className="overline">Scrivici</div>
            <h2 className="font-heading text-2xl text-risco-ink">Richiedi informazioni</h2>
            <div>
              <label className="text-sm text-stone-700">Nome e cognome *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-form-name" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
            </div>
            <div>
              <label className="text-sm text-stone-700">Email *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-form-email" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
            </div>
            <div>
              <label className="text-sm text-stone-700">Messaggio</label>
              <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-form-message" className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none" />
            </div>
            <button type="submit" disabled={sending} data-testid="contact-form-submit" className="w-full py-3 rounded-md bg-risco-accent hover:bg-risco-accent-dark disabled:opacity-50 text-white font-semibold">
              {sending ? "Invio..." : "Invia richiesta"}
            </button>
            <p className="text-xs text-stone-500">Inviando il form dichiari di aver letto la Privacy Policy.</p>
            {/* Honeypot antibot: nascosto lato UI, se compilato il backend scarta la richiesta. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
              <label htmlFor="ct-hp-website">Sito web (non compilare)</label>
              <input
                type="text"
                id="ct-hp-website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
